import { compareAsc } from 'date-fns/compareAsc';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  TaskAssignmentSummary,
  TaskKind,
  TaskProgressState,
} from 'uplisting-frontend/models/task';

type Task = ModelRegistry['task'];

export enum TaskMarkState {
  settled = 'settled',
  covered = 'covered',
  awaiting = 'awaiting',
  uncovered = 'uncovered',
  urgent = 'urgent',
  unscheduled = 'unscheduled',
}

export interface TaskDay {
  tasks: Task[];
  state: TaskMarkState;
}

export const STATE_LABELS: Record<TaskMarkState, string> = {
  [TaskMarkState.settled]: 'action.tasks.statuses.done',
  [TaskMarkState.covered]: 'action.tasks.assignment_states.accepted',
  [TaskMarkState.awaiting]:
    'action.tasks.assignment_states.awaiting_acceptance',
  [TaskMarkState.uncovered]: 'action.tasks.assignment_states.declined',
  [TaskMarkState.urgent]: 'calendar.tasks.states.urgent',
  [TaskMarkState.unscheduled]: 'action.tasks.assignment_states.unassigned',
};

// worst first
const STATE_RANK: TaskMarkState[] = [
  TaskMarkState.urgent,
  TaskMarkState.uncovered,
  TaskMarkState.awaiting,
  TaskMarkState.unscheduled,
  TaskMarkState.covered,
  TaskMarkState.settled,
];

const KIND_ORDER: TaskKind[] = [
  TaskKind.changeover,
  TaskKind.cleaning,
  TaskKind.maintenance,
  TaskKind.damageReport,
  TaskKind.compliance,
  TaskKind.dispute,
  TaskKind.custom,
];

function kindRank({ kind }: Task): number {
  const rank = KIND_ORDER.indexOf(kind);

  return rank === -1 ? KIND_ORDER.length : rank;
}

function compareTasks(a: Task, b: Task): number {
  return (
    kindRank(a) - kindRank(b) ||
    compareAsc(a.dueAtAsDate as Date, b.dueAtAsDate as Date)
  );
}

function isDueByTomorrow({ dueDay }: Task, today: Date): boolean {
  return !!dueDay && differenceInCalendarDays(dueDay, today) <= 1;
}

export function markState(task: Task, today: Date): TaskMarkState {
  const { progressState, assignmentState } = task;

  if (progressState === TaskProgressState.finished) {
    return TaskMarkState.settled;
  }

  if (progressState !== TaskProgressState.pending) {
    return TaskMarkState.covered;
  }

  if (assignmentState === TaskAssignmentSummary.awaitingAcceptance) {
    return TaskMarkState.awaiting;
  }

  if (isDueByTomorrow(task, today)) {
    return TaskMarkState.urgent;
  }

  return assignmentState === TaskAssignmentSummary.declined
    ? TaskMarkState.uncovered
    : TaskMarkState.unscheduled;
}

export function worstState(tasks: Task[], today: Date): TaskMarkState {
  const states = new Set(tasks.map((task) => markState(task, today)));

  return STATE_RANK.find((state) => states.has(state)) ?? TaskMarkState.settled;
}

export function tasksByDay(
  tasks: Task[],
  dates: Date[],
  today: Date,
): (TaskDay | undefined)[] {
  const [start] = dates;

  if (!start) {
    return [];
  }

  const byDay = dates.map((): Task[] => []);

  for (const task of tasks) {
    if (task.dueDay && !task.isCancelled) {
      byDay[differenceInCalendarDays(task.dueDay, start)]?.push(task);
    }
  }

  return byDay.map((forDay) =>
    forDay.length
      ? { tasks: forDay.sort(compareTasks), state: worstState(forDay, today) }
      : undefined,
  );
}
